import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { isEqual } from 'lodash';
import { RefreshCcw, SquareArrowOutUpRight, XIcon } from 'lucide-react';
import { toJS } from 'mobx';
import { Row, Col, Button, SimpleAlert, DefaultTooltip, FormBody, FormFooter, FormSectionCollapsible, List, ListItem, DefaultPopover, Separator, FormCard } from 'twyne-new.ui';

import { useAppAlert } from '@Components/AppAlert';
import Formsy from '@Components/form-new/FormContainer'
import { Select } from '@Components/pure/form-fields/Select';
import { buttonLabels } from '@Components/rules/options';
import routeStore from 'modules/cms/stores/route'
import appStore from 'stores/app';
import history from 'utils/history';

import Heading from '../../components/Heading';
import { useFormStore } from '../../store';
import * as fieldsUtil from '../fieldsUtil'

import FormBuilder from './FormBuilder'
import PaletteComponent from './Palette'

export default class FieldsFormComponent extends React.Component {
  state = {
    definition: [],
    allFields: [],
    selectedFields: {},
    htmlEditorErrors: [],
    updatedFields: [],
    fieldsWithRulesUpdated: [],
    customFields: [],
    alertInfoSync: false,
    syncedFields: false,
    eventConflict: null
  }

  handleConflict = (event) => {
    this.setState({ eventConflict: event })
  }

  componentWillMount() {
    const data = toJS(this.props.data);

    if (data) {
      data.definition = data.definition || []
      data.step = data.step || 0
      this.setState(data)
    }
  }

  onChangeField = (field, added = false) => {
    if (!['label', 'html', 'submit', 'previous_step_button', 'timer_submit', 'heading'].includes(field)) {
      const { selectedFields } = this.state
      selectedFields[field] = added
      this.setState({ selectedFields })
    }
  }

  handleCancel = () => {
    if (this.props.onCancel) {
      this.props.onCancel();
    } else {
      history.push('/offer/forms');
    }
  }

  showAlertInfoSync = () => {
    this.setState({ alertInfoSync: true })

    setTimeout(() => {
      this.setState({ alertInfoSync: false })
    }, 3000)
  }

  resetSyncFields = () => {
    this.setState({ updatedFields: [], fieldsWithRulesUpdated: [] })
  }

  syncFieldsForm = async () => {
    const { allFields, definition, updatedFields, fieldsWithRulesUpdated } = this.state
    const customFields = allFields.find((fields) => fields.name === "custom_user_fields").fields
    this.setState({ syncingFields: true })
    
    try {
      for (let step of definition) {
        for (let row of step) {
          if (row.cols) {
            for (let col of row.cols) {

              if (col.custom_field && customFields) {
                const newValue = { field: col.field, fieldsUpdated: [] }
                const customFieldUpdated = customFields.find((cf) => cf.value === col.field);

                if (customFieldUpdated) {
                  if (customFieldUpdated.field_type !== col.field_type) {
                    switch (customFieldUpdated.field_type) {
                      case "integer":
                        col.field_component = "number";
                        break;
                      case "input":
                        col.field_component = "text";
                        break;
                      case "select":
                        col.field_component = "select";
                        break;
                      case "boolean":
                        col.field_component = "checkbox_unique";
                        break;
                    }

                    col.field_type = customFieldUpdated.field_type
                    newValue.fieldsUpdated.push('field_type')
                  }

                  if (!isEqual(customFieldUpdated.field_options, col.field_options)) {
                    col.field_options = customFieldUpdated.field_options
                    newValue.fieldsUpdated.push('field_options')
                  }

                  newValue.fieldsUpdated.length && updatedFields.push(newValue)
                }
              }

            }
          }
        }
      }

      for (let [indexStep, step] of definition.entries()) {
        for (let row of step) {
          if (row.cols) {
            for (let col of row.cols) {
              const populate = col.controls && col.controls.populate && col.controls.populate.actions || []
              const visibility = col.controls && col.controls.visibility && col.controls.visibility.actions || []

              for (let control of [populate, visibility]) {
                for (let [actionIndex, action] of control.entries()) {
                  if (action.rules && action.rules.groups) {
                    for (let [groupIndex, group] of action.rules.groups.entries()) {
                      if (group.rules) {
                        for (let [ruleIndex, rule] of group.rules.entries()) {

                          if (updatedFields.some(field => field.field === rule.key)) {
                            const typeRule = control === populate ? "populate" : "visibility"

                            const newValue = {
                              step: indexStep + 1,
                              ruleKey: rule.key,
                              action: actionIndex + 1,
                              group: groupIndex + 1,
                              rule: ruleIndex + 1,
                              field: col.field,
                              typeRule,
                            }

                            if (typeRule === "populate") newValue.actionType = action.type
                            fieldsWithRulesUpdated.push(newValue)
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }

      updatedFields.length && this.setState({ definition, updatedFields, fieldsWithRulesUpdated })
      this.setState({ syncedFields: true })
      this.showAlertInfoSync();
    } catch (e) {
      this.setState({ syncedFields: false })
    } finally {
      this.setState({ syncingFields: false })
    }
  }

  markAlertToReviwed = (fieldRevised, all = false) => {
    const { fieldsWithRulesUpdated } = this.state

    const newFieldsWithRulesUpdated = fieldsWithRulesUpdated.filter(field => {
      return all ? field.ruleKey !== fieldRevised.field : !isEqual(field, fieldRevised)
    })

    this.setState({ fieldsWithRulesUpdated: newFieldsWithRulesUpdated })
  }

  AlertItem = ({ fieldWithRuleUpdated }) => <>
    <ListItem className='twy-border-warning! border-2 relative twy-text-neutral'>
      <Button
        color='warning'
        variant='light'
        size='sm'
        className='absolute top-1/2 -translate-y-1/2 right-2'
        onClick={() => this.markAlertToReviwed(fieldWithRuleUpdated)}
      >
        <XIcon size={14} />
      </Button>
      <div className='max-w-[90%]'>
        Please, check the field <b>{fieldWithRuleUpdated.field}</b> at the step <b>{fieldWithRuleUpdated.step}</b> and look at the <b>{fieldWithRuleUpdated.typeRule}</b> tab and review {" "}
        {fieldWithRuleUpdated.typeRule === "populate" && <> selected <b>{fieldWithRuleUpdated.actionType}</b> at </>}
        the group rule <b>{fieldWithRuleUpdated.action}</b> and condition <b>{fieldWithRuleUpdated.rule}</b>.
      </div>
    </ListItem>
  </>

  AlertFieldsUpdated = ({ updatedField }) => {
    const { fieldsWithRulesUpdated } = this.state

    return fieldsWithRulesUpdated.some(fieldRule => fieldRule.ruleKey === updatedField.field) && <>
      <SimpleAlert variant="warning" onClick={() => this.markAlertToReviwed(updatedField, true)}>
        <FormSectionCollapsible
          title={<>The custom field <b>{updatedField.field}</b> has been updated since the last synchronization and fields that depend on it need to be review.</>}
          titleClass='text-base twy-text-warning!'
          triggerClass='py-0'
          contentClass='pb-0'
        >
          <List border>
            {fieldsWithRulesUpdated.filter(field => field.ruleKey === updatedField.field).map(fieldWithRuleUpdated => <this.AlertItem fieldWithRuleUpdated={fieldWithRuleUpdated} />)}
          </List>
        </FormSectionCollapsible>
      </SimpleAlert>
    </>
  }

  Palette = () => {
    const { lists: { customFields: { items, useLoad } } } = useFormStore()

    useLoad()

    useEffect(() => {
      const loadFields = async () => {
        const allFields = []

        allFields.push({ label: 'User Fields', name: 'user_fields', fields: fieldsUtil.userFields })

        const customFields = []
        for (const field of items) {
          if (field.key.indexOf("cf") === 0) {
            customFields.push({ value: field.key, label: `[${field.type}] ${field.name}`, field_type: field.type, field_options: field.options })
          }
        }
        allFields.push({ label: 'Custom Fields', name: 'custom_user_fields', fields: customFields })

        allFields.push({ label: 'Static Fields', name: 'static_fields', fields: fieldsUtil.staticFields })

        allFields.push({ label: 'Consent Fields', name: 'consent_fields', fields: fieldsUtil.consentFields })

        allFields.push({ label: 'Dynamic Fields', name: 'dynamic_fields', fields: fieldsUtil.dynamicContentFields })

        this.setState({ allFields, customFields: items })
      }
      loadFields()
    }, [items])

    return <PaletteComponent data={this.state.allFields} selectedFields={this.state.selectedFields} onChange={() => this.setState({})} />
  }

  Formsy = ({ children }) => {
    const { handleError, handleWarning } = useAppAlert()

    const handleValidSubmit = model => {
      if (this.state.fieldsWithRulesUpdated.length)
        return handleWarning("Fields needs to be reviewed", 'To continue review the updated fields after synchronization and close all alerts.')

      if (!this.state.definition.length)
        return handleWarning("Invalid steps", 'At least one step is required.')

      const submitByStep = this.state.definition.map(step => [
        step.some(row => row.cols.some(col => col.field === 'submit')),
        step.some(row => row.cols.some(col => !!col.auto_submit))
      ])

      const allStepsHasCustomContinueOrAutoSubmit = submitByStep.every(([isSubmit, hasAutoSubmit]) => isSubmit || hasAutoSubmit)
      const atLeastOneStepHasCustomContinue = submitByStep.some(([isSubmit]) => isSubmit)

      if (atLeastOneStepHasCustomContinue && !allStepsHasCustomContinueOrAutoSubmit)
        return handleError("Invalid steps", 'Make sure all steps has one "Custom Submit Button" or at least one component with "Auto Submit Form" enabled or remove it all from them.')

      const step = this.state.definition.length
      let { conversion_step } = this.state
      conversion_step = !conversion_step || conversion_step > step ? step : conversion_step

      this.props.handleSubmit(Object.assign(this.state, {
        ...model,
        step,
        conversion_step,
        custom_continue: allStepsHasCustomContinueOrAutoSubmit
      }))

      this.resetSyncFields();
    }

    return <Formsy onValidSubmit={handleValidSubmit} id='form-fields'>
      {children}
    </Formsy>
  }

  useSitePath = post_id => {
    const [sitePath, setSitePath] = useState()
    const formId = this.state.id

    useEffect(() => {
      !async function loadRoute() {
        const hashFormParam = btoa(`fid:${formId}:force`)
        const routes = await routeStore.all(appStore.site.id).then(res => res.data && res.data.rows);
        const route = routes
          .find(d => d.entity_id === post_id)

        let sitePath = null
        if (route) {
          const url = new URL(`https://${appStore.site.domain}${route.path}`)
          url.searchParams.set('__testab_form', hashFormParam)
          if (process.env.NODE_ENV === 'development') {
            url.port = 3002
            url.protocol = 'http'
          }
          sitePath = url.href
        }

        setSitePath(sitePath)
      }()
    }, [post_id, formId])

    return sitePath
  }

  Preview = () => {
    const { lists: { landers: { useLoad, items } } } = useFormStore()
    const formId = this.state.id
    const [selectedLander, setSelectedLander] = useState(() => {
      const landersSelected = JSON.parse(localStorage.getItem('landers_selected') || '{}')
      return landersSelected[formId] || null
    })

    const [open, setOpen] = useState(false)
    useLoad()

    useEffect(() => {
      const landersSelected = JSON.parse(localStorage.getItem('landers_selected') || '{}')

      localStorage.setItem('landers_selected', JSON.stringify({ ...landersSelected, [formId]: selectedLander }))
    }, [selectedLander, formId])

    const landerOptions = useMemo(() => items.map(({ id, title, content_type }) => ({ value: id, label: `[${content_type.toUpperCase()}][#${id}] - ${title}` })), [items])

    const sitePath = this.useSitePath(selectedLander)

    const handleOpen = useCallback(() => {
      window.open(sitePath, '_blank')
      setOpen(false)
    }, [sitePath])

    return <>
      <DefaultPopover
        wrapperProps={{ open, onOpenChange: setOpen }}
        trigger={<DefaultTooltip content='Preview' triggerProps={{ asChild: true }}>
          <Button color="accent" variant='outline'>
            <SquareArrowOutUpRight size='16' />
          </Button>
        </DefaultTooltip>}
      >
        Select the lander so see the preview
        <Separator className='my-2' />
        <Row>
          <Col size="12">
            <Select
              options={landerOptions}
              value={selectedLander}
              onChange={option => setSelectedLander(option?.value || null)}
            />
          </Col>
          <Col size="12">
            <Button block color="accent" onClick={handleOpen} disabled={!selectedLander}>Open</Button>
          </Col>
        </Row>
      </DefaultPopover>
    </>
  }

  render() {
    const { definition, selectedFields, updatedFields, alertInfoSync, syncedFields, eventConflict } = this.state
    const disableForm = eventConflict === 'conflict' || eventConflict === 'checking'
    const disabled = this.state.htmlEditorErrors.length > 1 || disableForm
    return <>
      <Heading data={this.props.data}>
        <div className='flex gap-2 justify-end items-center'>
          {alertInfoSync && <SimpleAlert className='py-2'>
            {updatedFields.length ? "Custom fields updated successfully, save the form to continue." : "No custom fields were found to sync."}
          </SimpleAlert>}
          {this.props.children}
          <this.Preview />
          <DefaultTooltip content='Sync' wrapperProps={{ asChild: true }}>
            <Button color={syncedFields ? "neutral" : "accent"} variant='outline' onClick={this.syncFieldsForm} disabled={syncedFields} className={this.state.syncingFields ? 'animate-spin' : ''}>
              <RefreshCcw size='16' />
            </Button>
          </DefaultTooltip>
        </div>
      </Heading>
      <this.Formsy>
        <FormBody>
          <FormCard classNameBody='pr-0!'>
            <Row className='gap-0'>
              <Col size="3">
                <this.Palette />
              </Col>
              <Col size="9" className='border-l twy-border-base pl-4 pr-2'>
                <div className='h-[calc(100vh-15.2rem)] overflow-y-auto pr-2 mr-[-4px]'>
                  <FormBuilder onConflict={this.handleConflict} customFields={this.state.customFields} selectedFields={selectedFields} data={definition} onChangeField={this.onChangeField} onChange={definition => this.setState({ definition })}>
                    {!!updatedFields.length && updatedFields.map((updatedField, idx) => <this.AlertFieldsUpdated key={idx} updatedField={updatedField} />)}
                  </FormBuilder>
                </div>
              </Col>
            </Row>
          </FormCard>
          <FormFooter>
            <DefaultTooltip content={disableForm ? 'You can\'t save the form fields with rules conflict!' : ''} wrapperProps={{ asChild: true }}>
              <Button type="submit" color='accent' disabled={disabled}>{buttonLabels[eventConflict] || 'Save'}</Button>
            </DefaultTooltip>
            <Button onClick={this.handleCancel} variant='ghost'>Cancel</Button>
          </FormFooter>
        </FormBody>
      </this.Formsy>
    </>
  }
}

FieldsFormComponent.propTypes = {
  handleSubmit: PropTypes.func,
  data: PropTypes.any
};
